import { getErrorMessage } from "api/errors";
import { useReward } from "api/rewards";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useCallback, useMemo } from "react";
import { AddRewardCategory } from "../RewardCategoriesForm/AddRewardCategory";
import { EditRewardCategory } from "../RewardCategoriesForm/EditRewardCategory";
import { ModalMode } from "./ListOfRewardCategories";

interface RewardCategoriesModalsProps {
  close: () => void;
  selectedModal?: {
    mode: number | null;
    category?: string;
    isOpen: boolean;
  };
}

export function RewardCategoriesModals({
  close,
  selectedModal,
}: RewardCategoriesModalsProps) {
  const { message } = useMessage();
  const { activateRewardCategory, deactivateRewardCategory } = useReward();
  const { notify } = useNotifications();

  const messages = useMemo(
    () => ({
      activateRewardCategory: {
        successMessage: "rewardCategoryActivated",
        endpoint: activateRewardCategory,
      },
      deactivateRewardCategory: {
        successMessage: "rewardCategoryDeactivated",
        endpoint: deactivateRewardCategory,
      },
    }),
    [activateRewardCategory, deactivateRewardCategory],
  );

  const submitModal = useCallback(
    async (
      mode: "activateRewardCategory" | "deactivateRewardCategory",
      category: string,
    ) => {
      const successMessage = () => messages[mode]?.successMessage;
      const endpoint = async () => messages[mode]?.endpoint?.(category);
      try {
        const response = await endpoint();
        if (response) {
          close();
          notify(message({ id: successMessage() }));
        }
      } catch (error) {
        notify(getErrorMessage(error));
      }
    },
    [close, message, messages, notify],
  );

  return (
    <>
      {selectedModal?.mode === ModalMode.Activate ? (
        <ConfirmationModal
          action={() =>
            submitModal(
              "activateRewardCategory",
              selectedModal?.category as string,
            )
          }
          close={close}
          header="activateRewardCategoryHeader"
          info="activateRewardCategoryInfo"
          open={selectedModal.isOpen}
          data-test-id="activate-reward-category-modal"
          modalWidth={500}
        />
      ) : selectedModal?.mode === ModalMode.Deactivate ? (
        <ConfirmationModal
          action={() =>
            submitModal(
              "deactivateRewardCategory",
              selectedModal?.category as string,
            )
          }
          close={close}
          header="thisRewardCategoryWillBeDeactivated"
          info="deactivateThisRewardCategory"
          open={selectedModal.isOpen}
          data-test-id="deactivate-reward-category-modal"
          modalWidth={500}
        />
      ) : selectedModal?.mode === ModalMode.Add ? (
        <AddRewardCategory open={selectedModal.isOpen} onClose={close} />
      ) : selectedModal?.mode === ModalMode.Edit ? (
        <EditRewardCategory
          categoryId={selectedModal.category!}
          open={selectedModal.isOpen}
          onClose={close}
        />
      ) : null}
    </>
  );
}
